/**
 * Blog Posts Registry
 *
 * To add a new blog post:
 * 1. Create a numbered post file in this folder using oldest-first order
 * 2. Add the exported BlogPost object to BLOG_POSTS
 * 3. The post will automatically appear on the blog listing page
 *
 * Posts are sorted by date (newest first) automatically by getAllPosts().
 */

import { BlogPost, BlogPostPreview, calculateReadingTime } from '../types';
import { theEndOfOpenLoopAiPost } from './001-the-end-of-open-loop-ai';
import { aboutUsPost } from './002-about-us';
import { closingTheLoopWithoutSlowingTheRequestPost } from './003-closing-the-loop-without-slowing-the-request';
import { howWeAccidentallyTrainedThe1ModelRouterPost } from './004-how-we-accidentally-trained-the-1-model-router';
import { yourAiAgentIsWritingEssaysToTickBoxesPost } from './005-your-ai-agent-is-writing-essays-to-tick-boxes';

export const BLOG_POSTS: BlogPost[] = [
  yourAiAgentIsWritingEssaysToTickBoxesPost,
  howWeAccidentallyTrainedThe1ModelRouterPost,
  closingTheLoopWithoutSlowingTheRequestPost,
  aboutUsPost,
  theEndOfOpenLoopAiPost,
];

/**
 * Get all published posts sorted by date (newest first)
 */
export function getAllPosts(): BlogPostPreview[] {
  return BLOG_POSTS
    .filter(post => post.published)
    .map(({ content, ...meta }) => ({
      ...meta,
      readingTime: meta.readingTime || calculateReadingTime(content),
    }))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

/**
 * Get a single post by slug
 */
export function getPostBySlug(slug: string): BlogPost | undefined {
  const post = BLOG_POSTS.find(p => p.slug === slug && (p.published || process.env.NODE_ENV === 'development'));
  if (post) {
    return {
      ...post,
      readingTime: post.readingTime || calculateReadingTime(post.content),
    };
  }
  return undefined;
}

/**
 * Get posts by tag
 */
export function getPostsByTag(tag: string): BlogPostPreview[] {
  return getAllPosts().filter(post => post.tags.includes(tag));
}

/**
 * Get all unique tags
 */
export function getAllTags(): string[] {
  const tags = new Set<string>();
  BLOG_POSTS.filter(p => p.published).forEach(post => {
    post.tags.forEach(tag => tags.add(tag));
  });
  return Array.from(tags).sort();
}
